import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { format } from 'date-fns/format';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { isTimezoneEqualToLocal } from '__app/timezone';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getBusinessTimeZone } from 'store/authentication/selectors';
import { isValidDate } from '__components/Inputs/DateTimeInput/helpers';

type UseTimezoneWarningReturn = {
  date: Date | null;
  businessTimezone: string;
  isBusinessTimeMatchesLocal: boolean;
  timezoneWarning: string;
  businessTimeZoneString: string;
  localDateString: string;
};

export const useTimezoneWarning = (date: Date) => {
  const { t } = useTranslation();

  const businessTimezone = useSelector(getBusinessTimeZone);

  return useMemo(() => {
    if (!date || !businessTimezone || !isValidDate(date)) {
      return {
        date: null,
        businessTimezone,
        isBusinessTimeMatchesLocal: false,
        timezoneWarning: '',
        businessTimeZoneString: '',
        localDateString: '',
        offset: '',
        businessDateString: '',
        localToBusinessOffsetMs: 0,
      };
    }

    const isBusinessTimeMatchesLocal = isTimezoneEqualToLocal(businessTimezone);

    const offset = formatInTimeZone(date, businessTimezone, 'XXX');

    const localToBusinessOffsetMs =
      getTimezoneOffset(businessTimezone, date) -
      getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone, date);

    const businessTimeZoneString = `GMT${offset}`;

    const localDateString = format(date, 'LLLL d, y p');

    const businessDateString = formatInTimeZone(
      date,
      businessTimezone,
      'LLLL d, y p'
    );

    const timezoneWarning = isBusinessTimeMatchesLocal
      ? ''
      : `${t(
          'Your timezone differs from the business timezone ({{ businessTimeZoneString }}). This will be scheduled at {{ localDateString }} in your timezone.',
          {
            businessTimeZoneString,
            localDateString,
          }
        )}`;

    return {
      date,
      businessTimezone,
      isBusinessTimeMatchesLocal,
      timezoneWarning,
      businessTimeZoneString,
      localDateString,
      offset,
      localToBusinessOffsetMs,
      businessDateString,
    };
  }, [t, businessTimezone, date]);
};

type TimezoneWarningProps = {
  date: Date;
  composeMessage?: (props: UseTimezoneWarningReturn) => string;
};

export const TimezoneWarning = ({
  date,
  composeMessage,
}: TimezoneWarningProps) => {
  const result = useTimezoneWarning(date);

  if (result.isBusinessTimeMatchesLocal || !date || !isValidDate(date)) {
    return null;
  }

  return (
    <div className="flex gap-10">
      <Icon icon="custom-globe" size="md" color="background-dark" />
      <Typography size="sm" color="font-placeholder" fontStyle="italic">
        {composeMessage ? composeMessage(result) : result.timezoneWarning}
      </Typography>
    </div>
  );
};
